import React, { useCallback, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import Flex from "../../../../../../components/structure/Flex";
import Button from "../../../../../../components/ui/Button";
import Icon from "../../../../../../components/ui/Icon";
import { AuthContext } from "../../../../../../contexts/auth";
import { ProfileContext } from "../../../../../../contexts/profile";
import UserProfileHeader from "../UserProfileHeader/UserProfileHeader";
import DeleteAccount from "./components/DeleteAccount";
import UpcomingBooking from "./components/UpcomingBooking";
import UserStats from "./components/UserStats";

interface MenuItem {
  title: string;
  description: string;
  onClick: () => void;
}

const MobileDashboard: React.FC = () => {
  const history = useHistory();
  const { profile } = useContext(ProfileContext);
  const { logout } = useContext(AuthContext);
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const handleNavigation = useCallback(
    (path: string) => {
      history.push(path);
    },
    [history]
  );

  const handleHelpClick = useCallback(() => {
    window.open(
      "https://api.whatsapp.com/send?phone=919289229822&text=Hi%20Zostel",
      "_blank",
      "noopener,noreferrer"
    );
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    history.push("/");
  }, [logout, history]);

  const toggleModal = useCallback(() => {
    setModalVisible((prev) => !prev);
  }, []);

  const menuItems: MenuItem[] = [
    {
      title: "Edit Profile",
      description: "Manage your Zostel profile",
      onClick: () => handleNavigation("/account"),
    },
    {
      title: "Your Bookings",
      description: "Navigate through all your bookings",
      onClick: () => handleNavigation("/bookings"),
    },
    {
      title: "Contact Us",
      description: "Contact for any help",
      onClick: handleHelpClick,
    },
  ];

  const renderMenuItem = useCallback(
    ({ title, description, onClick }: MenuItem) => (
      <button
        key={title}
        onClick={onClick}
        className="flex flex-col p-2 mb-8 space-y-1 border border-gray-200 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-500"
        aria-label={title}
      >
        <Flex items="center" className="w-full px-1 mb-2" justify="between">
          <Flex col>
            <span className="text-xs font-medium">{title}</span>
            <span className="text-xs font-hairline">{description}</span>
          </Flex>
          <Icon className="text-2xl">arrow_right</Icon>
        </Flex>
      </button>
    ),
    []
  );

  return (
    <section className="w-full overflow-y-auto rounded-lg sm:p-4 sm:mt-8 sm:pb-4 bg-gray-50">
      <UserProfileHeader profile={profile} />

      <Flex col className="px-4 bg-white rounded-lg shadow-md">
        {menuItems.map(renderMenuItem)}
      </Flex>

      <UserStats />
      <UpcomingBooking />

      <Flex col className="p-4">
        <Button
          type="outlined"
          icon="logout"
          onClick={handleLogout}
          iconPosition="end"
          className="mx-2 my-6"
          aria-label="Log Out"
        >
          Log Out
        </Button>
        <button
          onClick={toggleModal}
          className="w-full p-2 text-center text-orange-600 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
          aria-label="Delete Account"
        >
          Delete Account
        </button>
      </Flex>

      {modalVisible && <DeleteAccount toggleModal={toggleModal} />}
    </section>
  );
};

export default React.memo(MobileDashboard);
